'use client'

import { useState, useCallback } from 'react'
import { AUTH_API_PATHS } from '@/lib/auth/paths'
import { useStore } from '@/store'
import { queryClient } from '@/lib/queryClient'
import {
  ADMIN_IMPERSONATION_STATUS_QUERY_KEY,
  useImpersonationStatus,
} from '@/Auth/useImpersonationStatus'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

export function ImpersonationBanner() {
  const [isStopping, setIsStopping] = useState(false)
  const [stopError, setStopError] = useState<string | null>(null)
  const user = useStore(state => state.user)
  const authResolved = useStore(state => state.authResolved)
  const { activeImpersonation, isImpersonatingCurrentUser } =
    useImpersonationStatus()

  const stopImpersonation = useCallback(async () => {
    setIsStopping(true)
    setStopError(null)
    try {
      const response = await fetchWithCloudflarePreclearance(
        AUTH_API_PATHS.ADMIN_IMPERSONATE_SYNC,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      const result = (await response.json().catch(() => null)) as {
        success?: boolean
        error?: string | null
        redirectUrl?: string | null
      } | null

      if (!response.ok || !result?.success || !result.redirectUrl) {
        throw new Error(
          result?.error ?? 'Failed to prepare impersonation cleanup'
        )
      }

      useStore.getState().setUser(null)
      useStore.getState().setShoppingCartId(null)
      queryClient.removeQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      void queryClient.invalidateQueries({
        queryKey: ADMIN_IMPERSONATION_STATUS_QUERY_KEY,
      })

      window.location.assign(result.redirectUrl)
    } catch (error) {
      console.error('Failed to stop impersonation:', error)
      setStopError(
        'Could not confirm the impersonation session ended. Please try again.'
      )
      setIsStopping(false)
    }
  }, [])

  if (!activeImpersonation) return null
  if (!authResolved || !isImpersonatingCurrentUser) return null

  const customerEmail = activeImpersonation.customerEmail ?? user?.email ?? ''
  const customerName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`.trim()
      : null
  const label = customerName
    ? `${customerName} (${customerEmail})`
    : customerEmail

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black px-4 py-2 text-sm font-medium flex items-center justify-between shadow-md">
        <span>
          Impersonating <strong>{label}</strong> &mdash; Customer #
          {activeImpersonation.customerId}
        </span>
        <button
          onClick={stopImpersonation}
          disabled={isStopping}
          className="ml-4 bg-black text-white px-3 py-1 rounded text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {isStopping ? 'Stopping...' : 'Stop Impersonating'}
        </button>
      </div>
      {stopError && (
        <div className="fixed top-10 left-0 right-0 z-[9999] bg-red-700 text-white px-4 py-2 text-sm font-medium shadow-md">
          {stopError}
        </div>
      )}
      <div className="h-10" aria-hidden="true" />
    </>
  )
}
